import axios from "axios";
import { CONFIGURATIONS } from "./envConfig";
import { errorMessages } from "../constants/frontendErrorMessages";
// Import store lazily to avoid circular dependencies
import showToast from "../utils/toastNotifications";

export const api = axios.create({
  baseURL: `${CONFIGURATIONS.API_BASE_URL}/`,
  withCredentials: true, // Ensures cookies are included in all requests
});

// Store pending requests with their cancel tokens
let pendingRequests = [];

// Listen for auth state changes
window.addEventListener("auth-state-changed", () => {
  console.log(
    `Canceling ${pendingRequests.length} pending requests due to auth state change`,
  );

  // Cancel any pending requests
  pendingRequests.forEach((request) => {
    try {
      request.cancel();
    } catch (err) {
      console.error("Error canceling request:", err);
    }
  });

  // Clear the pending requests array
  pendingRequests = [];

  window.location.reload();
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Create a cancel token for this request
    const source = axios.CancelToken.source();
    config.cancelToken = source.token;

    // Track this request (only for non-auth requests)
    // Skip tracking for auth-related endpoints like login/logout
    const isAuthEndpoint = config.url.includes("/auth/", "/user/inquiry");
    if (!isAuthEndpoint) {
      pendingRequests.push({
        url: config.url,
        cancel: () =>
          source.cancel("Request canceled due to auth state change"),
      });
    }

    // Add timezone header for behavioral analysis
    config.headers["x-client-timezone"] =
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Add auth token
    if (!config?.voidAuth) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    } else {
      delete config.voidAuth;
    }

    return config;
  },
  (error) => {
    console.error("Request setup error:", error);
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  async (response) => {
    // Remove the request from pending requests list
    const index = pendingRequests.findIndex(
      (req) => req.url === response.config.url,
    );
    if (index > -1) {
      pendingRequests.splice(index, 1);
    }
    return response;
  },

  async (error) => {
    console.error("Response error:", error);

    // Remove from pending even on error
    if (error.config) {
      const index = pendingRequests.findIndex(
        (req) => req.url === error.config.url,
      );
      if (index > -1) {
        pendingRequests.splice(index, 1);
      }
    }

    // Check if this error was caused by cancellation due to auth change
    if (axios.isCancel(error)) {
      console.log("Request canceled:", error.message);
      return Promise.reject(error);
    }

    // Extract error details from the response
    const errorData = error.response?.data;
    const statusCode = errorData?.statusCode;
    const errorMessage = errorData?.message;
    const isAuthEndpoint = error.config.url.includes("auth/");
    // Import auth actions on demand to avoid circular dependencies

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log("Unauthorized response detected");

      if (!isAuthEndpoint) {
        // For non-auth endpoints, check for specific error codes
        if (statusCode === "5060") {
          // User is not approved yet error
          console.log("User not approved error detected");

          // Set persistent error message for toast on login page
          if (window.location.pathname !== "/signin") {
            localStorage.setItem("auth_error", "Session Expired");
          }

          // Create a custom error object with approval-specific information
          error.message = errorMessage || "Your account is pending approval.";
          error.code = statusCode;
          error.name = "ApprovalPendingError";

          // Dispatch a specific action for approval pending
          // Use dynamic import to avoid circular dependency
          import("../store/slices/authSlice").then((authModule) => {
            import("../store").then((storeModule) => {
              storeModule.default.dispatch(authModule.logout());
            });
          });
        } else {
          // Generic 401 error handling
          console.log("Handling generic 401 with sign-in modal");

          // Show persistent toast notification via localStorage ONLY if we are not already on the signin page
          if (window.location.pathname !== "/signin") {
            localStorage.setItem("auth_error", "Session Expired");
            localStorage.setItem("session_expired", "true");
            // Force redirect to signin page
            window.location.href = "/signin";
          } else {
            // We are already on signin page, just clear tokens
            localStorage.removeItem("token");
            localStorage.removeItem("auth");
          }

          // Use dynamic import to avoid circular dependency
          // Clear local storage immediately
          if (window.location.pathname !== "/signin") {
            localStorage.removeItem("token");
            localStorage.removeItem("auth");
          }

          // Use dynamic import to update state (best effort)
          import("../store/slices/authSlice").then((authModule) => {
            import("../store").then((storeModule) => {
              storeModule.default.dispatch(
                authModule.handleSessionExpiration(),
              );
            });
          });

          // Update error details
          error.message = "Authentication required. Please sign in.";
          error.code = "401";
          error.name = "AuthError";
        }
      } else {
        // For auth endpoints themselves (like failed login attempts),
        error.message = errorMessage || errorMessages.ERROR_OCCURRED;
        error.code = statusCode || `${error.response?.status}`;
        error.name = "AuthEndpointError";
      }
    } else if (error.response?.status === 403) {
      console.log("Forbidden response detected");
      if (!isAuthEndpoint) {
        // For non-auth endpoints, check for specific error codes
        if (statusCode === "5055") {
          console.log("User rejected error detected");
          // Use dynamic import to avoid circular dependency
          import("../store/slices/authSlice").then((authModule) => {
            import("../store").then((storeModule) => {
              storeModule.default.dispatch(authModule.logout());
            });
          });
          error.message = errorMessages.SOMETHING_WENT_WRONG;
          error.code = statusCode;
          error.name = "AccountRejectedError";
          showToast("error", errorMessages.SOMETHING_WENT_WRONG);
        } else {
          error.message = errorMessage || errorMessages.ERROR_OCCURRED;
          error.code = statusCode || `${error.response?.status}`;
          error.name = "AuthEndpointError";
        }
      } else {
        // Update error details
        error.message = errorMessage || errorMessages.PERMISSION_DENIED;
        error.code = statusCode || `${error.response?.status}`;
        error.name = "AuthError";
      }
    } else if (error.response) {
      // Existing response with error status
      const data = error.response.data;
      error.message = data?.message || errorMessages.ERROR_OCCURRED;
      error.code = `${error.response?.status}` || "500";
      error.name = data?.statusCode || "Axios Error";
    } else {
      console.log("Network or setup error");
      // Error setting up the request
      error.message = error.message || errorMessages.UNKNOWN_ERROR_OCCURRED;
      error.code = "500";

      return;
    }

    return Promise.reject(error);
  },
);
